/**
 * Tiny i18n layer. English source strings are the keys; `tr` holds the Turkish text and `en`
 * only overrides where the key itself is not what English readers should see.
 * Turkish is the default. The choice lives in localStorage (before sign-in) and users/{uid}.lang.
 */
import { useSyncExternalStore } from 'react';

export type Lang = 'tr' | 'en';
export const LANGS: { id: Lang; label: string }[] = [{ id: 'tr', label: 'Türkçe' }, { id: 'en', label: 'English' }];
const KEY = 'duoplate.lang';

function stored(): Lang {
  try { const v = localStorage.getItem(KEY); if (v === 'tr' || v === 'en') return v; } catch { /* storage blocked */ }
  return 'tr';
}

let lang: Lang = stored();
const listeners = new Set<() => void>();
if (typeof document !== 'undefined') document.documentElement.lang = lang;

export const getLang = (): Lang => lang;

export function setLang(next: Lang): void {
  if (next !== 'tr' && next !== 'en') return;
  try { localStorage.setItem(KEY, next); } catch { /* storage blocked */ }
  if (next === lang) return;
  lang = next;
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  listeners.forEach((f) => f());
}

/** Re-render on a language change (used once at the app root). */
export function useLang(): Lang {
  return useSyncExternalStore((f) => { listeners.add(f); return () => listeners.delete(f); }, getLang);
}

/** Translate an English source string; {name} placeholders are filled from vars. */
export function t(s: string, vars?: Record<string, string | number>): string {
  let out = (lang === 'tr' ? tr[s] : en[s]) ?? s;
  if (vars) out = out.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
  return out;
}

/** BCP 47 tag for dates, numbers and speech. */
export const locale = (): string => (lang === 'tr' ? 'tr-TR' : 'en-GB');
export const speechLang = (): string => (lang === 'tr' ? 'tr-TR' : 'en-US');

const en: Record<string, string> = {};

export const tr: Record<string, string> = {
  "Something went wrong on this screen": "Bu ekranda bir sorun oluştu",
  "Your data is safe. Reload to continue.": "Verilerin güvende. Devam etmek için yeniden yükle.",
  "Reload": "Yeniden yükle",
  "Add a task": "Görev ekle",
  "Loading your duo…": "Duo'n yükleniyor…",
  "Today": "Bugün",
  "Inbox": "Gelen",
  "Coach": "Koç",
  "Plan": "Plan",
  "Shop": "Alışveriş",
  "More": "Diğer",
  "Email or password is wrong.": "E-posta veya şifre hatalı.",
  "That email address does not look right.": "Bu e-posta adresi doğru görünmüyor.",
  "Too many attempts. Wait a minute and try again.": "Çok fazla deneme. Bir dakika bekleyip tekrar dene.",
  "No connection. Check your internet and try again.": "Bağlantı yok. İnternetini kontrol edip tekrar dene.",
  "Something went wrong. Try again.": "Bir sorun oluştu. Tekrar dene.",
  "That account belongs to another app. Create a DuoPlate account with a different email.": "Bu hesap başka bir uygulamaya ait. Farklı bir e-postayla DuoPlate hesabı oluştur.",
  "Use at least 8 characters for the password.": "Şifre en az 8 karakter olmalı.",
  "This email already has an account. Sign in, reset your password, or use a different email.": "Bu e-postayla zaten bir hesap var. Giriş yap, şifreni sıfırla ya da farklı bir e-posta kullan.",
  "Type your email above first.": "Önce yukarıya e-postanı yaz.",
  "If that email has an account, a reset link is on its way.": "Bu e-postaya ait bir hesap varsa sıfırlama bağlantısı yolda.",
  "Make your day with your partner.": "Gününü partnerinle planla.",
  "Meals, routines and date nights, planned together, with a coach that adjusts the plan for both of you. Works on your own too.": "Öğünler, rutinler ve randevu akşamları birlikte planlanır; koç planı ikiniz için ayarlar. Tek başına da kullanılabilir.",
  "Sign in": "Giriş yap",
  "Create account": "Hesap oluştur",
  "Your first name": "Adın",
  "Email": "E-posta",
  "Password": "Şifre",
  "One moment…": "Bir saniye…",
  "Forgot your password?": "Şifreni mi unuttun?",
  "How we handle your data:": "Verilerini nasıl işliyoruz:",
  "privacy notice (Aydınlatma Metni)": "Aydınlatma Metni",
  "Too many attempts. Wait a while and try again.": "Çok fazla deneme. Biraz bekleyip tekrar dene.",
  "Could not create the account. Try again.": "Hesap oluşturulamadı. Tekrar dene.",
  "Finish your profile first.": "Önce profilini tamamla.",
  "You are already in a duo.": "Zaten bir duo'dasın.",
  "Create a duo first.": "Önce bir duo oluştur.",
  "Your duo is already full.": "Duo'n zaten dolu.",
  "Could not create an invite. Try again.": "Davet oluşturulamadı. Tekrar dene.",
  "Invite codes are 6 letters and numbers.": "Davet kodları 6 harf ve rakamdan oluşur.",
  "Leave your current duo before joining another.": "Başka birine katılmadan önce mevcut duo'ndan ayrıl.",
  "This invite code is wrong or has expired. Ask your partner for a new one.": "Bu davet kodu yanlış ya da süresi dolmuş. Partnerinden yenisini iste.",
  "That duo no longer exists.": "Bu duo artık yok.",
  "That duo is already full.": "Bu duo zaten dolu.",
  "Sign in first.": "Önce giriş yap.",
  "This account is not a DuoPlate account.": "Bu hesap bir DuoPlate hesabı değil.",
  "{name} & partner": "{name} & partneri",
  "Our duo": "Bizim duo",
  "Step 2 of 2": "Adım 2/2",
  "Pair up": "Eşleş",
  "I have an invite code": "Davet kodum var",
  "Start my plan": "Planımı başlat",
  "Works on your own. Invite your partner any time from Settings to turn it into a duo.": "Tek başına da çalışır. Duo'ya çevirmek için partnerini istediğin zaman Ayarlar'dan davet et.",
  "A duo is you and your partner: one shared plan, two sets of portions, shared tasks, date nights and shopping. Starting alone? Create your plan now and invite your partner whenever you like.": "Duo, sen ve partnerin demek: ortak bir plan, iki ayrı porsiyon, ortak görevler, randevu akşamları ve alışveriş. Tek mi başlıyorsun? Planını şimdi oluştur, partnerini istediğin zaman davet et.",
  "Invite code": "Davet kodu",
  "e.g. K7Q2MX": "ör. K7Q2MX",
  "Joining…": "Katılınıyor…",
  "Join my partner": "Partnerime katıl",
  "Duo name": "Duo adı",
  "Creating…": "Oluşturuluyor…",
  "Track": "Takip",
  "Routine": "Rutin",
  "General": "Genel",
  "Progress": "İlerleme",
  "Weigh-ins, body scans and your trend": "Tartılar, vücut analizleri ve gidişatın",
  "Cycle": "Döngü",
  "Phases, period log and food for each phase": "Evreler, regl kaydı ve her evreye uygun yemek",
  "Habits & tasks": "Alışkanlıklar ve görevler",
  "Water, walks, pills, workouts: add or edit": "Su, yürüyüş, takviye, antrenman: ekle veya düzenle",
  "Guide": "Rehber",
  "Drinks, cheat meal, swaps and portions": "İçecekler, kaçamak öğün, alternatifler ve porsiyonlar",
  "Ask the AI to plan, swap or move anything": "Yapay zekâdan her şeyi planlamasını, değiştirmesini veya taşımasını iste",
  "Settings": "Ayarlar",
  "Profile, your day, reminders, partner, account": "Profil, günün, hatırlatmalar, partner, hesap",
  "Admin": "Yönetici",
  "Accounts and who can use the AI coach": "Hesaplar ve yapay zekâ koçunu kimlerin kullanabileceği",
  "How the plan works": "Plan nasıl işler",
  "Drinks": "İçecekler",
  "Cheat meal": "Kaçamak öğün",
  "Idea": "Fikir",
  "Higher track": "Yüksek porsiyon",
  "Lower track": "Düşük porsiyon",
  "Rules of the week": "Haftanın kuralları",
  "Swaps": "Alternatifler",
  "Portion shortcuts": "Porsiyon kısayolları",
  "General guidance for healthy adults, not medical advice. Check with a doctor if you are pregnant, have a medical condition or take medication.": "Sağlıklı yetişkinler için genel bilgidir, tıbbi tavsiye değildir. Hamileysen, bir sağlık sorunun varsa ya da ilaç kullanıyorsan doktoruna danış.",
  "Both": "İkimiz",
  "You": "Sen",
  "Partner": "Partner",
  "no reminder": "hatırlatma yok",
  "Shared with your duo": "Duo'nla ortak",
  "Add": "Ekle",
  "No habits yet. Add your first one.": "Henüz alışkanlık yok. İlkini ekle.",
  "Every day": "Her gün",
  "Once, {date}": "Bir kez, {date}",
  "Sun": "Paz",
  "Mon": "Pzt",
  "Tue": "Sal",
  "Wed": "Çar",
  "Thu": "Per",
  "Fri": "Cum",
  "Sat": "Cmt",
  "Give it a name.": "Bir ad ver.",
  "Pick at least one day.": "En az bir gün seç.",
  "Pick at least one day of the month.": "Ayın en az bir gününü seç.",
  "Could not save. Check your connection.": "Kaydedilemedi. Bağlantını kontrol et.",
  "Edit task": "Görevi düzenle",
  "New task": "Yeni görev",
  "What do you want to do?": "Ne yapmak istiyorsun?",
  "Name": "Ad",
  "Together": "Birlikte",
  "Date night": "Randevu akşamı",
  "Dinner out": "Dışarıda akşam yemeği",
  "Movie together": "Birlikte film",
  "Coffee date": "Kahve buluşması",
  "Walk together": "Birlikte yürüyüş",
  "Weekend trip": "Hafta sonu gezisi",
  "Icon": "Simge",
  "On the timeline": "Zaman çizelgesinde",
  "Inbox (no time)": "Gelen kutusu (saatsiz)",
  "Time": "Saat",
  "Repeat": "Tekrar",
  "Once": "Bir kez",
  "Some weekdays": "Haftanın bazı günleri",
  "Monthly": "Aylık",
  "Date": "Tarih",
  "Days of the month (comma separated)": "Ayın günleri (virgülle ayır)",
  "How long": "Süre",
  "min": "dk",
  "h": "sa",
  "For": "Kimin için",
  "Me": "Ben",
  "Note": "Not",
  "Send a reminder": "Hatırlatma gönder",
  "Save": "Kaydet",
  "Add to inbox": "Gelen kutusuna ekle",
  "Add to timeline": "Zaman çizelgesine ekle",
  "Delete": "Sil",
  "Cancel": "Vazgeç",
  "Things to do, no time yet": "Yapılacaklar, henüz saati yok",
  "Your inbox is empty. Park ideas here, then give them a time when you are ready.": "Gelen kutun boş. Fikirlerini buraya bırak, hazır olduğunda saat ver.",
  "Schedule": "Planla",
  "Put on timeline": "Zaman çizelgesine koy",
  "Done": "Tamamlananlar",
  "Clear": "Kaldır",
  "Add a to-do…": "Yapılacak ekle…",
  "Mark {title} done": "{title} tamamlandı olarak işaretle",
  "Mark {title} not done": "{title} tamamlanmadı olarak işaretle",
  "Shopping · week {n} of the menu": "Alışveriş · menünün {n}. haftası",
  "Previous week": "Önceki hafta",
  "Next week": "Sonraki hafta",
  "{done}/{total} in the basket": "Sepette {done}/{total}",
  "Search {item} on {store}": "{store}'da {item} ara",
  "Item": "Ürün",
  "Add an item": "Ürün ekle",
  "Amount": "Miktar",
  "Protein & dairy": "Protein ve süt ürünleri",
  "Carbs & pantry": "Karbonhidrat ve kiler",
  "Vegetables": "Sebzeler",
  "Fruit": "Meyveler",
  "Fish (buy fresh)": "Balık (taze al)",
  "Eggs": "Yumurta",
  "Chicken breast (göğüs / bonfile)": "Tavuk göğsü (bonfile)",
  "Plain kaymaksız yoğurt": "Kaymaksız sade yoğurt",
  "Milk 1.5%": "Süt %1,5",
  "Süzme peynir or lor": "Süzme peynir veya lor",
  "Whole-wheat bread": "Tam buğday ekmeği",
  "Whole-wheat lavaş": "Tam buğday lavaş",
  "Oats": "Yulaf",
  "Tomatoes": "Domates",
  "Cucumbers": "Salatalık",
  "Green peppers": "Sivri biber",
  "Lettuce / göbek": "Göbek marul",
  "Parsley": "Maydanoz",
  "Onions, garlic": "Soğan, sarımsak",
  "Lemons": "Limon",
  "Carrots, potatoes": "Havuç, patates",
  "Apples": "Elma",
  "Bananas": "Muz",
  "Nar (pomegranate)": "Nar",
  "Skinless chicken thigh (pirzola)": "Derisiz tavuk pirzola",
  "Lean kıyma": "Yağsız kıyma",
  "Red lentils": "Kırmızı mercimek",
  "Spinach or beyaz lahana": "Ispanak veya beyaz lahana",
  "Hamsi (Thursday)": "Hamsi (perşembe)",
  "Lean beef kuşbaşı": "Yağsız dana kuşbaşı",
  "Red lentils (ezogelin)": "Kırmızı mercimek (ezogelin)",
  "Pırasa (leeks)": "Pırasa",
  "Broccoli": "Brokoli",
  "Palamut (Thursday)": "Palamut (perşembe)",
  "Salmon (Friday)": "Somon (cuma)",
  "Rice": "Pirinç",
  "Spinach": "Ispanak",
  "Mushrooms": "Mantar",
  "Levrek or çipura (Thursday)": "Levrek veya çipura (perşembe)",
  "2 × 2 kg (strain half into süzme)": "2 × 2 kg (yarısını süzme yap)",
  "1 loaf (freeze half)": "1 somun (yarısını dondur)",
  "1 pack": "1 paket",
  "1 kg (lasts ~2 weeks)": "1 kg (~2 hafta yeter)",
  "1 bunch": "1 demet",
  "1 kg + 1 head": "1 kg + 1 baş",
  "1 kg each": "her biri 1 kg",
  "1 kg / 1 head": "1 kg / 1 baş",
  "Rise and shine": "Günaydın, kalk vakti",
  "Wind down": "Gün sonu, rahatla",
  "Now": "Şimdi",
  "For {name}": "{name} için",
  "g protein": "g protein",
  "{name} did it": "{name} yaptı",
  "{name} not yet": "{name} henüz yapmadı",
  "{d} free": "{d} boş",
  "a good moment for a walk": "yürüyüş için iyi bir an",
  "Back to today": "Bugüne dön",
  "{n} missed task today. Move it to later?": "Bugün {n} kaçırılan görev var. Sonraya taşıyalım mı?",
  "{n} missed tasks today. Move them to later?": "Bugün {n} kaçırılan görev var. Sonraya taşıyalım mı?",
  "Replan": "Yeniden planla",
  "No meal plan for this day yet.": "Bu gün için henüz yemek planı yok.",
  "day {n}": "{n}. gün",
  "Good night": "İyi geceler",
  "Good morning": "Günaydın",
  "Good afternoon": "Tünaydın",
  "Good evening": "İyi akşamlar",
  "Breakfast": "Kahvaltı",
  "Lunch": "Öğle yemeği",
  "Snack": "Ara öğün",
  "Dinner": "Akşam yemeği",
  "Pre-workout": "Antrenman öncesi",
  "Post-workout": "Antrenman sonrası",
  "Cheat meal ": "Kaçamak öğün",
  "day streak": "günlük seri",
  "both did it": "ikiniz de yaptınız",
  "Cheer sent 👏": "Alkış gönderildi 👏",
  "👏 Cheer {name}": "👏 Alkış gönder: {name}",
  "Could not send the cheer. Try again later.": "Alkış gönderilemedi. Sonra tekrar dene.",
  "Sunday review": "Pazar değerlendirmesi",
  "week of {date}": "{date} haftası",
  "Reminders are on for this device.": "Bu cihazda hatırlatmalar açık.",
  "Notifications are blocked. Allow them for DuoPlate in your browser or phone settings, then try again.": "Bildirimler engelli. Tarayıcı veya telefon ayarlarından DuoPlate'e izin verip tekrar dene.",
  "This browser cannot receive push reminders. Try Chrome on Android or the installed app on iPhone.": "Bu tarayıcı hatırlatma alamıyor. Android'de Chrome'u ya da iPhone'da yüklü uygulamayı dene.",
  "On iPhone, add DuoPlate to your Home Screen first (Share → Add to Home Screen), open it from there, then turn reminders on.": "iPhone'da önce DuoPlate'i Ana Ekran'a ekle (Paylaş → Ana Ekrana Ekle), oradan aç, sonra hatırlatmaları aç.",
  "Could not turn on reminders. Check your connection and try again.": "Hatırlatmalar açılamadı. Bağlantını kontrol edip tekrar dene.",
  "Join me on DuoPlate: {link} (code {code})": "DuoPlate'te bana katıl: {link} (kod {code})",
  "Copy this link: {link}": "Bu bağlantıyı kopyala: {link}",
  "Join me on DuoPlate (code {code})": "DuoPlate'te bana katıl (kod {code})",
  "Invite copied. Send it to your partner.": "Davet kopyalandı. Partnerine gönder.",
  "Your data was downloaded as a JSON file.": "Verilerin JSON dosyası olarak indirildi.",
  "Could not prepare your data. Try again.": "Verilerin hazırlanamadı. Tekrar dene.",
  "Cycle tracking is off and your cycle log is deleted.": "Döngü takibi kapatıldı ve döngü kaydın silindi.",
  "Could not delete the account. Sign out, sign in again and retry (recent sign-in is required).": "Hesap silinemedi. Çıkış yapıp tekrar giriş yap ve yeniden dene (yakın zamanda giriş gerekir).",
  "Profile saved. Use Plan → Update portions to apply new targets to the meal plan.": "Profil kaydedildi. Yeni hedefleri yemek planına uygulamak için Plan → Porsiyonları güncelle'yi kullan.",
  "Password changed.": "Şifre değiştirildi.",
  "Profile": "Profil",
  "Close": "Kapat",
  "Save profile": "Profili kaydet",
  "Language": "Dil",
  "Reminders, the coach and the Sunday review follow this language.": "Hatırlatmalar, koç ve pazar değerlendirmesi bu dili kullanır.",
  "Edit profile": "Profili düzenle",
  "kcal": "kcal",
  "protein": "protein",
  "water": "su",
  "Your day": "Günün",
  "The first and last bubble on your timeline.": "Zaman çizelgendeki ilk ve son balon.",
  "Reminders": "Hatırlatmalar",
  "On. Reminders arrive at each task's and meal's time.": "Açık. Hatırlatmalar her görevin ve öğünün saatinde gelir.",
  "Add this device too": "Bu cihazı da ekle",
  "Turn all off": "Hepsini kapat",
  "Turn on reminders": "Hatırlatmaları aç",
  "Duo": "Duo",
  "Invite my partner": "Partnerimi davet et",
  "Share": "Paylaş",
  "Copy": "Kopyala",
  "Leave this duo": "Bu duo'dan ayrıl",
  "Leave duo": "Duo'dan ayrıl",
  "Plan & extras": "Plan ve ekstralar",
  "Privacy & account": "Gizlilik ve hesap",
  "Download my data": "Verilerimi indir",
  "Stop cycle tracking and delete the log": "Döngü takibini durdur ve kaydı sil",
  "This turns cycle tracking off, withdraws your cycle consent, stops sharing your phase and permanently deletes your cycle log.": "Bu işlem döngü takibini kapatır, döngü onayını geri çeker, evre paylaşımını durdurur ve döngü kaydını kalıcı olarak siler.",
  "Delete cycle data": "Döngü verilerini sil",
  "Sign out": "Çıkış yap",
  "Delete everything": "Her şeyi sil",
  "Change password": "Şifreyi değiştir",
  "Wake up": "Uyanış",
  "Current password": "Mevcut şifre",
  "New password": "Yeni şifre",
  "Meal reminders": "Öğün hatırlatmaları",
  "Habit and task reminders": "Alışkanlık ve görev hatırlatmaları",
  "On iPhone: tap Share → Add to Home Screen, open DuoPlate from the Home Screen, then turn reminders on here.": "iPhone'da: Paylaş → Ana Ekrana Ekle'ye dokun, DuoPlate'i Ana Ekran'dan aç, sonra hatırlatmaları buradan aç.",
  "with {name}": "{name} ile",
  "waiting for your partner": "partnerin bekleniyor",
  "Invite code · valid {n} days": "Davet kodu · {n} gün geçerli",
  "Share my cycle phase with my partner (open Cycle once to update it)": "Döngü evremi partnerimle paylaş (güncellemek için Döngü'yü bir kez aç)",
  "Leaving removes you from the shared plan, tasks and shopping list.": "Ayrılırsan ortak plandan, görevlerden ve alışveriş listesinden çıkarılırsın.",
  "{name} keeps them.": "Bunlar {name} ile kalır.",
  "As the only member, the duo and its data are deleted.": "Tek üye olduğun için duo ve verileri silinir.",
  "Your duo is on DuoPlate Plus.": "Duo'nuz DuoPlate Plus'ta.",
  "You are on the free plan. DuoPlate Plus (₺149 a month or ₺999 a year per duo) is coming soon with the AI coach, Sunday reviews and date ideas.": "Ücretsiz plandasın. DuoPlate Plus (duo başına aylık ₺149 veya yıllık ₺999) yakında: yapay zekâ koçu, pazar değerlendirmeleri ve buluşma fikirleriyle.",
  "Consent given": "Onay tarihi",
  "text version {v}": "metin sürümü {v}",
  "body data and cycle log": "vücut verileri ve döngü kaydı",
  "body data": "vücut verileri",
  "Delete my account.": "Hesabımı sil.",
  "This permanently deletes your profile, cycle log, measurements and push tokens, removes you from your duo, and withdraws your consent. Type DELETE to confirm.": "Bu işlem profilini, döngü kaydını, ölçümlerini ve bildirim anahtarlarını kalıcı olarak siler, seni duo'dan çıkarır ve onayını geri çeker. Onaylamak için DELETE yaz.",
  "Type DELETE to confirm": "Onaylamak için DELETE yaz",
  "Current password is not right.": "Mevcut şifre doğru değil.",
  "Pick a longer password (8+ characters).": "Daha uzun bir şifre seç (8+ karakter).",
  "Could not change the password. Try again.": "Şifre değiştirilemedi. Tekrar dene.",
  "DuoPlate needs your consent for weight and body data: your targets are calculated from it.": "DuoPlate'in kilo ve vücut verilerin için onayına ihtiyacı var: hedeflerin bunlardan hesaplanır.",
  "Tick the cycle consent, or turn cycle tracking off.": "Döngü onayını işaretle ya da döngü takibini kapat.",
  "Could not save. Check your connection and try again.": "Kaydedilemedi. Bağlantını kontrol edip tekrar dene.",
  "About you": "Hakkında",
  "Lose fat": "Yağ yak",
  "Maintain": "Koru",
  "Your daily targets": "Günlük hedeflerin",
  "steps": "adım",
  "Workouts": "Antrenmanlar",
  "Classes you go to every week. They become tasks with reminders, and your partner can share the same class.": "Her hafta gittiğin dersler. Hatırlatmalı görevlere dönüşür; partnerin de aynı dersi paylaşabilir.",
  "Remove": "Kaldır",
  "Add a workout": "Antrenman ekle",
  "Cycle tracking": "Döngü takibi",
  "Your cycle log stays private to you. Your partner only ever sees your current phase, and only if you turn that on.": "Döngü kaydın sadece sende kalır. Partnerin yalnızca mevcut evreni görür, o da sadece sen açarsan.",
  "Privacy": "Gizlilik",
  "Step 1 of 2": "Adım 1/2",
  "Set up your profile": "Profilini oluştur",
  "First name": "Adın",
  "Birth year": "Doğum yılı",
  "Height (cm)": "Boy (cm)",
  "Weight (kg)": "Kilo (kg)",
  "BMR from your body-composition scale (optional)": "Vücut analiz tartından BMR (isteğe bağlı)",
  "Activity": "Aktivite",
  "Sex (for the calorie formula)": "Cinsiyet (kalori formülü için)",
  "Goal": "Hedef",
  "Female": "Kadın",
  "Male": "Erkek",
  "e.g. 1217": "ör. 1217",
  "Mi and similar scales show \"Basal metabolic rate\". It is more accurate than the formula.": "Mi ve benzeri tartılar \"Bazal metabolizma hızı\" gösterir. Formülden daha doğrudur.",
  "Desk job, little walking": "Masa başı iş, az yürüyüş",
  "Desk job + 2–3 light classes a week": "Masa başı iş + haftada 2–3 hafif ders",
  "On your feet often, or 3–4 workouts a week": "Sık ayakta, ya da haftada 3–4 antrenman",
  "Physical job, or hard training most days": "Fiziksel iş, ya da çoğu gün yoğun antrenman",
  "Estimated maintenance ≈ {n} kcal.": "Tahmini koruma ≈ {n} kcal.",
  "That is a deficit of about {n} kcal a day.": "Bu, günde yaklaşık {n} kcal açık demek.",
  "Set targets myself (e.g. from a coach)": "Hedefleri kendim belirleyeyim (ör. bir koçtan)",
  "Water (L)": "Su (L)",
  "Protein (g)": "Protein (g)",
  "Steps": "Adım",
  "DuoPlate is for adults ({n}+). Calorie-deficit plans are not suitable for younger people.": "DuoPlate yetişkinler içindir ({n}+). Kalori açığı planları daha gençler için uygun değildir.",
  "Fill in birth year, height and weight to see your targets.": "Hedeflerini görmek için doğum yılı, boy ve kilonu gir.",
  "Workout name": "Antrenman adı",
  "Track my cycle and adjust food hints by phase (optional)": "Döngümü takip et ve yemek önerilerini evreye göre ayarla (isteğe bağlı)",
  "Read how DuoPlate uses your data in the": "DuoPlate'in verilerini nasıl kullandığını okuyabilirsin:",
  "Save and continue": "Kaydet ve devam et",
  "Enter your weight in kg.": "Kilonu kg olarak gir.",
  "Saved.": "Kaydedildi.",
  "Private to you": "Sadece sana özel",
  "latest": "son",
  "kg / week": "kg / hafta",
  "Add a measurement": "Ölçüm ekle",
  "Friday weigh-in: weight only. Body scan on the 15th and 28th: add what your scale shows.": "Cuma tartısı: sadece kilo. Ayın 15'i ve 28'inde vücut analizi: tartının gösterdiklerini ekle.",
  "Fat %": "Yağ %",
  "Visceral": "Viseral",
  "Muscle": "Kas",
  "Waist": "Bel",
  "Body fat %": "Vücut yağı %",
  "Visceral fat": "Viseral yağ",
  "Muscle (kg)": "Kas (kg)",
  "Waist (cm)": "Bel (cm)",
  "since {date}": "{date} tarihinden beri",
  "Slower than 0.25 kg a week. If this holds for two weeks, cut 100–150 kcal or add 2,000 steps.": "Haftada 0,25 kg'dan yavaş. İki hafta böyle giderse 100–150 kcal azalt ya da 2.000 adım ekle.",
  "Faster than 1 kg a week. After the first week, add 100–150 kcal so you keep muscle.": "Haftada 1 kg'dan hızlı. İlk haftadan sonra kasını korumak için 100–150 kcal ekle.",
  "On track. Judge the trend over weeks, not single days.": "Yolundasın. Tek tek günlere değil, haftalık gidişata bak.",
  "Delete {date}": "{date} kaydını sil",
  "Weight from {a} to {b} kg": "Kilo {a} kg'dan {b} kg'a",
  "Give the workout a name.": "Antrenmana bir ad ver.",
  "Could not create the plan. Check your connection.": "Plan oluşturulamadı. Bağlantını kontrol et.",
  "Portions updated for everyone from today on.": "Porsiyonlar bugünden itibaren herkes için güncellendi.",
  "Drink 250 ml before and 250 ml after. Eat your snack 60–90 minutes before.": "Öncesinde 250 ml, sonrasında 250 ml su iç. Ara öğününü 60–90 dakika önce ye.",
  "Added {name} on {date}.": "{name}, {date} tarihine eklendi.",
  "Added {name} every {day}.": "{name} her {day} eklendi.",
  "Plan created: {a} – {b}.": "Plan oluşturuldu: {a} – {b}.",
  "Added 30 more days from {date}.": "{date} tarihinden itibaren 30 gün daha eklendi.",
  "Meal plan": "Yemek planı",
  "Create your meal plan": "Yemek planını oluştur",
  "The menu is built around Sunday prep, so a Sunday start works best.": "Menü pazar hazırlığına göre kurulu; en iyisi pazar günü başlamak.",
  "No workout on this day.": "Bu gün antrenman yok.",
  "Edit": "Düzenle",
  "Only this day": "Sadece bu gün",
  "Save workout": "Antrenmanı kaydet",
  "No plan on this day. Pick a highlighted day.": "Bu gün plan yok. İşaretli bir gün seç.",
  "Manage plan": "Planı yönet",
  "Add the next 30 days": "Sonraki 30 günü ekle",
  "Use this after a partner joins or after changing targets in Settings.": "Partner katıldıktan ya da Ayarlar'da hedefleri değiştirdikten sonra bunu kullan.",
  "Workout": "Antrenman",
  "Meal prep": "Yemek hazırlığı",
  "30 days of Turkish meals from the DuoPlate template: breakfast, two snacks, lunch and dinner, with portions sized to each of your targets. Dinners are cooked double for the next day's lunch, and Saturday night is the cheat meal.": "DuoPlate şablonundan 30 günlük Türk mutfağı: kahvaltı, iki ara öğün, öğle ve akşam yemeği; porsiyonlar ikinizin hedeflerine göre. Akşam yemekleri ertesi günün öğlesi için çift pişer, cumartesi akşamı kaçamak öğündür.",
  "Start on": "Başlangıç",
  "Create 30-day plan": "30 günlük plan oluştur",
  "Previous month": "Önceki ay",
  "Next month": "Sonraki ay",
  "this day only": "sadece bu gün",
  "Minutes": "Dakika",
  "e.g. Tennis": "ör. Tenis",
  "Every {day}": "Her {day}",
  "Week {n}": "{n}. hafta",
  "The plan runs until {date}.": "Plan {date} tarihine kadar sürüyor.",
  "Update portions for both of us": "İkimiz için porsiyonları güncelle",
  "Update portions": "Porsiyonları güncelle",
  "Cycle tracking is off. Turn it on in Settings → Profile.": "Döngü takibi kapalı. Ayarlar → Profil'den aç.",
  "Loading…": "Yükleniyor…",
  "Log your last period": "Son regl dönemini kaydet",
  "Period started": "Regl başladı",
  "Period ended": "Regl bitti",
  "Saving…": "Kaydediliyor…",
  "Sharing": "Paylaşım",
  "Logged periods": "Kaydedilen dönemler",
  "Phases and food": "Evreler ve yemek",
  "General guidance, not medical advice. Very irregular cycles, very heavy bleeding or severe pain are worth a gynaecologist visit.": "Genel bilgidir, tıbbi tavsiye değildir. Çok düzensiz döngüler, çok yoğun kanama veya şiddetli ağrı için bir jinekoloğa görün.",
  "Day {d} of ~{n}": "~{n} günün {d}. günü",
  "estimated": "tahmini",
  "Next period around": "Sonraki regl yaklaşık",
  "+{n} kcal allowed today": "bugün +{n} kcal serbest",
  "Training:": "Antrenman:",
  "Pick the first day of your last period in the calendar, then tap \"Period started\". DuoPlate learns your cycle length from there.": "Takvimde son reglinin ilk gününü seç, sonra \"Regl başladı\"ya dokun. DuoPlate döngü uzunluğunu buradan öğrenir.",
  "Period": "Regl",
  "Predicted": "Tahmini",
  "Symptoms": "Belirtiler",
  "Flow": "Akış",
  "Show {name} my current phase and a hint (never the log, dates or symptoms)": "{name} mevcut evremi ve bir ipucu görsün (kayıt, tarihler veya belirtiler asla görünmez)",
  "my partner": "partnerim",
  "Average cycle {c} days, period {p} days, from {n} logged periods.": "Ortalama döngü {c} gün, regl {p} gün ({n} kayıtlı dönemden).",
  "ongoing": "devam ediyor",
  "spotting": "lekelenme",
  "light": "hafif",
  "medium": "orta",
  "heavy": "yoğun",
  "Cramps": "Kramp",
  "Bloating": "Şişkinlik",
  "Cravings": "Aşerme",
  "Low mood": "Düşük ruh hali",
  "Irritable": "Huzursuzluk",
  "Headache": "Baş ağrısı",
  "Tired": "Yorgunluk",
  "Acne": "Sivilce",
  "Tender breasts": "Göğüs hassasiyeti",
  "Bad sleep": "Kötü uyku",
  "Iron and warmth": "Demir ve sıcaklık",
  "Follicular": "Foliküler",
  "Best days for the deficit": "Kalori açığı için en iyi günler",
  "Ovulation": "Yumurtlama",
  "Steady and hydrated": "Dengeli ve bol su",
  "Luteal": "Luteal",
  "Appetite up, plan for it": "İştah artar, ona göre planla",
  "Lean on iron: kırmızı mercimek, ıspanak, kıyma or dana, eggs. Add lemon or a mandalina for vitamin C so the iron absorbs.": "Demire yüklen: kırmızı mercimek, ıspanak, kıyma veya dana, yumurta. Demirin emilmesi için C vitamini olarak limon ya da mandalina ekle.",
  "Warm meals help: mercimek or ezogelin çorbası, tarhana.": "Sıcak yemekler iyi gelir: mercimek ya da ezogelin çorbası, tarhana.",
  "Keep çay away from meals (it blocks iron); have it an hour later.": "Çayı yemekten uzak tut (demir emilimini engeller); bir saat sonra iç.",
  "Cramps: 10 g walnuts or almonds and a banana give magnesium and potassium.": "Kramp: 10 g ceviz veya badem ve bir muz magnezyum ve potasyum sağlar.",
  "Gentle Pilates and walking are fine and often ease cramps. Skip intense sessions if you feel drained.": "Hafif Pilates ve yürüyüş uygundur, çoğu zaman krampı hafifletir. Bitkin hissediyorsan yoğun seansları atla.",
  "Energy and insulin sensitivity are usually best now: stick to the plan as written.": "Enerji ve insülin duyarlılığı genelde şimdi en iyi halinde: plana aynen uy.",
  "Good week to try a new dish or a slightly smaller dinner carb portion.": "Yeni bir yemek denemek ya da akşam karbonhidratını biraz azaltmak için iyi bir hafta.",
  "Strongest phase: push a little harder in Pilates and add an extra walk.": "En güçlü evre: Pilates'te biraz daha zorla ve fazladan bir yürüyüş ekle.",
  "Keep protein high and drink an extra glass of water.": "Proteini yüksek tut ve fazladan bir bardak su iç.",
  "Appetite can dip briefly; still eat the planned snacks.": "İştah kısa süre düşebilir; planlanan ara öğünleri yine de ye.",
  "Normal training. Warm up well: joints can be a bit looser around ovulation.": "Normal antrenman. İyi ısın: yumurtlama civarında eklemler biraz gevşek olabilir.",
  "Resting energy rises a little in this phase. You have about +120 kcal today: an extra apple + 10 g walnuts, or 150 g süzme yoğurt with cinnamon.": "Bu evrede dinlenme enerjisi biraz artar. Bugün yaklaşık +120 kcal hakkın var: fazladan bir elma + 10 g ceviz ya da tarçınlı 150 g süzme yoğurt.",
  "Cravings: 2 squares (10 g) of bitter chocolate are planned, not a failure.": "Aşerme: 2 kare (10 g) bitter çikolata plana dahil, başarısızlık değil.",
  "Go easy on salt, pickles and processed food to limit bloating; keep water high.": "Şişkinliği azaltmak için tuz, turşu ve işlenmiş gıdayı azalt; suyu bol tut.",
  "Complex carbs (bulgur, oats) at dinner can help sleep and mood.": "Akşam yemeğinde kompleks karbonhidrat (bulgur, yulaf) uyku ve ruh haline iyi gelebilir.",
  "Energy may drop in the last days. Keep Pilates, lower the intensity if needed.": "Son günlerde enerji düşebilir. Pilates'e devam et, gerekirse yoğunluğu azalt.",
  "We are eating out tomorrow night. Adjust the rest of the day.": "Yarın akşam dışarıda yiyoruz. Günün geri kalanını ayarla.",
  "Find us a cosy dinner place in Kadıköy for Saturday.": "Cumartesi için Kadıköy'de bize samimi bir akşam yemeği yeri bul.",
  "Move my Pilates on Tuesday to 18:00.": "Salı günkü Pilates'imi 18:00'e al.",
  "I'm always hungry around 16:00. What can I change?": "16:00 civarı hep acıkıyorum. Neyi değiştirebilirim?",
  "The AI coach isn't turned on for your account yet.": "Yapay zekâ koçu hesabında henüz açık değil.",
  "Your plan, on demand": "Planın, istediğin an",
  "Meet your coach ✨": "Koçunla tanış ✨",
  "Ask it to plan, swap or move anything: meals, pills, walks, Pilates, groceries. It changes the plan for both of you, and every change has an Undo.": "Her şeyi planlamasını, değiştirmesini veya taşımasını iste: öğünler, takviyeler, yürüyüşler, Pilates, alışveriş. Planı ikiniz için değiştirir ve her değişikliğin bir Geri al düğmesi vardır.",
  "Start": "Başla",
  "Details in the": "Ayrıntılar",
  "privacy notice": "Aydınlatma Metni'nde",
  "General guidance, not medical advice.": "Genel bilgidir, tıbbi tavsiye değildir.",
  "General guidance, not medical advice": "Genel bilgidir, tıbbi tavsiye değildir",
  "The coach could not answer. Try again.": "Koç cevap veremedi. Tekrar dene.",
  "Could not undo that change.": "Bu değişiklik geri alınamadı.",
  "Allow the microphone to talk to the coach.": "Koçla konuşmak için mikrofona izin ver.",
  "Plans, swaps and changes for you two": "İkiniz için planlar ve değişiklikler",
  "Read replies aloud": "Cevapları sesli oku",
  "New chat": "Yeni sohbet",
  "Unlimited messages": "Sınırsız mesaj",
  "{used}/{limit} messages this month": "Bu ay {used}/{limit} mesaj",
  "Stop listening": "Dinlemeyi durdur",
  "Speak": "Konuş",
  "Send": "Gönder",
  "Listening…": "Dinliyorum…",
  "Ask your coach…": "Koçuna sor…",
  "Coach is thinking": "Koç düşünüyor",
  "Ask anything about your meals, routine or plan.": "Öğünlerin, rutinin veya planın hakkında her şeyi sor.",
  "Tip: use the microphone on your keyboard to dictate.": "İpucu: yazdırmak için klavyendeki mikrofonu kullan.",
  "Turn the coach off": "Koçu kapat",
  "Undo": "Geri al",
  "The coach is not turned on for this account yet.": "Koç bu hesapta henüz açık değil.",
  "Agree to the coach privacy terms first.": "Önce koçun gizlilik koşullarını onayla.",
  "Create or join a duo first.": "Önce bir duo oluştur ya da katıl.",
  "That is the coach limit for today. It resets tomorrow.": "Bugünlük koç sınırına ulaştın. Yarın sıfırlanır.",
  "The coach is not configured yet.": "Koç henüz yapılandırılmadı.",
  "The coach is busy right now. Try again in a minute.": "Koç şu an meşgul. Bir dakika sonra tekrar dene.",
  "The coach hit a problem. Try again or start a new chat.": "Koç bir sorunla karşılaştı. Tekrar dene ya da yeni bir sohbet başlat.",
  "Write a message first.": "Önce bir mesaj yaz.",
  "Your duo has used this month's coach messages. They reset on the 1st.": "Duo'nuz bu ayın koç mesajlarını kullandı. Ayın 1'inde sıfırlanır.",
  "Invite your partner first.": "Önce partnerini davet et.",
  "That is enough cheering for today. Try again tomorrow.": "Bugünlük bu kadar alkış yeter. Yarın tekrar dene.",
  "Drink 500 ml water first. Eat slowly, stop at satisfied, walk after. Back on plan at Sunday breakfast; no skipping meals to make up for it.": "Önce 500 ml su iç. Yavaş ye, doyunca dur, sonra yürü. Pazar kahvaltısında plana dön; telafi için öğün atlama.",
  "Training tonight: eat this 60–90 minutes before class.": "Bu akşam antrenman var: bunu dersten 60–90 dakika önce ye.",
  "Right after your morning class.": "Sabah dersinden hemen sonra.",
  "Leftovers from last night (cooked double). Pack it if you are at work.": "Dün akşamdan kalan (çift pişirildi). İşteysen yanına al.",
  "From Sunday meal prep. Lunchbox if you are at work.": "Pazar hazırlığından. İşteysen beslenme kabına koy.",
  "Post-workout lunch: fresh and quick.": "Antrenman sonrası öğle yemeği: taze ve hızlı.",
  "Fresh lunch. Soup keeps 3 days, so make a big pot.": "Taze öğle yemeği. Çorba 3 gün dayanır, büyük bir tencere yap.",
  "Lunchbox if you are at work.": "İşteysen beslenme kabına koy.",
  "Use the chicken grilled at Sunday prep. A 10-minute dinner after class.": "Pazar hazırlığında ızgara yapılan tavuğu kullan. Dersten sonra 10 dakikalık bir akşam yemeği.",
  "Cook double: the second half is tomorrow's lunch.": "Çift pişir: ikinci yarısı yarının öğle yemeği.",
  "Fish night: buy it fresh today.": "Balık akşamı: bugün taze al.",
  "Brunch": "Geç kahvaltı",
  "Another country": "Başka bir ülke",
  "supermarket": "süpermarket",
  "This browser cannot share a location.": "Bu tarayıcı konum paylaşamıyor.",
  "Finding you…": "Konumun bulunuyor…",
  "Location not shared. The city is enough too.": "Konum paylaşılmadı. Şehir de yeterli.",
  "Country": "Ülke",
  "City or neighbourhood": "Şehir veya semt",
  "e.g. Kadıköy, Istanbul": "ör. Kadıköy, İstanbul",
  "Precise location saved (about 1 km).": "Konum kaydedildi (yaklaşık 1 km).",
  "Use my location": "Konumumu kullan",
  "Used to suggest markets and places near you. Your location is never shown to your partner.": "Sana yakın market ve mekân önermek için kullanılır. Konumun partnerine asla gösterilmez.",
  "Where you shop": "Alışveriş yaptığın yer",
  "Supermarkets near you": "Yakındaki süpermarketler",
  "Search {item}": "{item} ara",
  "Open in Maps": "Haritada aç",
  "Photo": "Fotoğraf",
  "Precise location saved (about 100 m).": "Konum kaydedildi (yaklaşık 100 m).",
  // @tr-end
};
